import {
  take,
  takeLatest,
  select,
  call,
  fork,
  cancel,
  put
} from "redux-saga/effects";
import { eventChannel } from "redux-saga";
import selectors from "selectors";
import { Types } from "actions/twilio-chat";
import browserHistory from "browser-history";
import { Client } from "@twilio/conversations";

// Helper to normalize message state across SDK versions
function normalizeMessageState(message) {
  return {
    author: message.author,
    body: message.body,
    timestamp: message.dateCreated?.toISOString() || message.dateCreated,
    attributes: message.attributes,
    index: message.index
  };
}

export default function* rootTwilioChatSaga() {
  yield takeLatest(
    Types.INITIALIZE_TWILIO_CHAT_CLIENT,
    function* ({ payload }) {
      try {
        const userId = yield select(state => selectors.getUserId(state));

        const chatClient = yield createTwilioChatClient();
        yield put({ type: Types.TWILIO_CHAT_READY });
        browserHistory.push("/");
        const startTask = yield startTwilioChat(payload, chatClient, userId);
        yield take(["auth/logout", "TWILIO_CHAT_ENDED"]);
        console.debug("ending chat");
        if (startTask) yield cancel(startTask);
      } catch (error) {
        console.error("Chat Error:", error);
      }
    }
  );
}

function createConversationsClient(token) {
  return new Promise((resolve, reject) => {
    // Saga-based client for global chat window. Hook-based client in use-twilio-chat.js serves text-chat page - these surfaces don't render simultaneously.
    const client = new Client(token);
    client.on("stateChanged", state => {
      if (state === "initialized") resolve(client);
      if (state === "failed")
        reject(new Error("Failed to initialize Twilio Conversations client"));
    });
  });
}

function* createTwilioChatClient() {
  console.debug("create TwilioChat Client");
  const twilioToken = yield select(state => selectors.getTwilioToken(state));
  const client = yield call(createConversationsClient, twilioToken);
  return client;
}

function* startTwilioChat(channelSid, chatClient, userId) {
  try {
    const channel = yield getConversation(chatClient, channelSid);
    if (!channel) {
      yield put({
        type: Types.TWILIO_CHAT_ERROR,
        error: "Could not connect to chat room"
      });
      throw Error("could not get chat channel");
    }

    const status = channel.status;

    if (status !== "joined") {
      yield channel.join();
    }
    yield put({
      type: Types.TWILIO_CHAT_PARTICIPENT_JOINED,
      payload: userId
    });
    yield put({ type: Types.TWILIO_CHAT_STARTED });
    yield call(getChatHistory, channel);

    const handlePageLeave = e => {
      e.preventDefault();
      e.returnValue = "";
      console.debug("leaving channel");
      channel.leave();
    };
    window.addEventListener("beforeunload", handlePageLeave);
    const channelEventsTask = yield fork(
      handleChatChannelEvents,
      channel,
      userId
    );
    yield take([
      "auth/logout",
      Types.CLOSE_TWILIO_CHAT,
      Types.START_TWILIO_CHAT,
      Types.END_TWILIO_CHAT
    ]);
    yield channel.leave();
    if (channelEventsTask) yield cancel(channelEventsTask);
    yield put({ type: Types.TWILIO_CHAT_ENDED });
    window.removeEventListener("beforeunload", handlePageLeave);
  } catch (error) {
    console.error(error);
    yield put({ type: Types.TWILIO_CHAT_ERROR, error });
    yield put({ type: Types.TWILIO_CHAT_ENDED, error });
  }
}

async function getConversation(chatClient, channelSid) {
  try {
    const conversation = await chatClient
      .getConversationBySid(channelSid)
      .catch(error => {
        console.error(error);
      });
    if (conversation) return conversation;
  } catch (error) {
    console.error("getConversation ERROR:", error);
  }
}

function* getChatHistory(channel) {
  const { items = [] } = yield channel.getMessages();
  const restoredMessages = items.map(message => normalizeMessageState(message));
  yield put({
    type: Types.TWILIO_CHAT_RESTORE_MESSAGES,
    payload: restoredMessages
  });
}
const chatEventChannel = (channel, userId) =>
  eventChannel(emit => {
    const typingTimeouts = {};

    channel.on("messageAdded", message => {
      emit({
        type: Types.TWILIO_CHAT_MESSAGE_ADDED,
        payload: normalizeMessageState(message)
      });
    });

    const joinEvent = "participantJoined";
    const leaveEvent = "participantLeft";

    channel.on(joinEvent, member => {
      emit({
        type: Types.TWILIO_CHAT_PARTICIPENT_JOINED,
        payload: member.identity
      });
      emit({
        type: Types.TWILIO_CHAT_MESSAGE_ADDED,
        payload: { body: "User Joined Chat" }
      });
    });

    channel.on(leaveEvent, member => {
      emit({
        type: Types.TWILIO_CHAT_PARTICIPENT_LEFT,
        payload: member.identity
      });
      emit({
        type: Types.TWILIO_CHAT_MESSAGE_ADDED,
        payload: { body: "User Left Chat" }
      });
    });

    channel.on("removed", () => {
      emit({
        type: Types.TWILIO_CHAT_MESSAGE_ADDED,
        payload: { body: "Chat Ended" }
      });
      emit({
        type: Types.END_TWILIO_CHAT
      });
    });

    channel.on("typingStarted", participant => {
      console.debug("typing started");
      // Clear any existing safety timeout for this participant
      if (typingTimeouts[participant.identity]) {
        clearTimeout(typingTimeouts[participant.identity]);
      }
      emit({
        type: Types.TWILIO_CHAT_TYPING_STARTED,
        payload: participant.identity
      });
      // Safety timeout: force-end if typingEnded never arrives
      // Handles: sudden disconnect, browser close, network loss
      typingTimeouts[participant.identity] = setTimeout(() => {
        emit({
          type: Types.TWILIO_CHAT_TYPING_ENDED,
          payload: participant.identity
        });
        delete typingTimeouts[participant.identity];
      }, 10000);
    });

    channel.on("typingEnded", participant => {
      // Cancel safety timeout since we got the normal event
      if (typingTimeouts[participant.identity]) {
        clearTimeout(typingTimeouts[participant.identity]);
        delete typingTimeouts[participant.identity];
      }
      emit({
        type: Types.TWILIO_CHAT_TYPING_ENDED,
        payload: participant.identity
      });
    });

    channel.on("participantUpdated", ({ participant, updateReasons }) => {
      if (
        participant.identity !== userId && updateReasons.includes("lastReadMessageIndex")
      ) {
        emit({
          type: Types.TWILIO_CHAT_PARTICIPANT_UPDATED,
          payload: {
            identity: participant.identity,
            lastReadMessageIndex: participant.lastReadMessageIndex,
            lastReadTimestamp:
              participant.lastReadTimestamp?.toISOString() ||
              participant.lastReadTimestamp
          }
        });
      }
    });

    return () => {
      Object.values(typingTimeouts).forEach(clearTimeout);
    };
  });

function* handleChatChannelEvents(channel, userId) {
  const userName = yield select(state => selectors.getUserName(state));
  yield fork(listenSendMessage, channel, userName, userId);
  yield fork(listenTypingSignal, channel);
  yield fork(listenReadHorizonUpdate, channel, userId);
  const channelActions = yield call(chatEventChannel, channel, userId);
  while (true) {
    const action = yield take(channelActions);
    yield put(action);
  }
}

function* listenSendMessage(channel, userName, userId) {
  while (true) {
    const { payload } = yield take(Types.TWILIO_SEND_MESSAGE);
    try {
      yield channel.sendMessage(payload, {
        authorName: userName,
        authorId: userId
      });
    } catch (error) {
      console.error(error);
    }
  }
}

function* listenTypingSignal(channel) {
  while (true) {
    yield take(Types.TWILIO_SEND_TYPING);
    try {
      yield channel.typing();
    } catch (error) {
      console.error("Error sending typing signal:", error);
    }
  }
}

function* listenReadHorizonUpdate(channel, userId) {
  let lastLocalReadIndex;
  while (true) {
    yield take(Types.TWILIO_UPDATE_READ_HORIZON);
    try {
      const messages = yield select(state => state.twilioChat.messages);
      const lastReceivedMessage = [...messages]
        .reverse()
        .find(msg => msg.author !== userId);

      if (lastReceivedMessage?.index !== undefined) {
        if (lastLocalReadIndex === undefined || lastReceivedMessage.index > lastLocalReadIndex) {
          yield channel.updateLastReadMessageIndex(lastReceivedMessage.index);
          lastLocalReadIndex = lastReceivedMessage.index;
        }
      }
    } catch (error) {
      console.error("Error updating read horizon:", error);
    }
  }
}
